import api from '@/services/api/client';
import {getV2Error} from '@/services/api/v2';
import {WyscoutImportMonths} from './wyscoutImportApi';

export type WyscoutClubRosterLinkState = 'new' | 'existing_external' | 'existing_squad';

export interface WyscoutClubRosterPlayer {
  wyscoutPlayerId: number;
  name: string;
  position: string;
  birthDate: string;
  nationality: string;
  photoUrl: string;
  linkState: WyscoutClubRosterLinkState;
  existingPlayerId: string | null;
  existingScope: 'external' | 'squad' | null;
}

export interface WyscoutClubRosterPreview {
  team: {wyscoutTeamId: number; name: string};
  players: WyscoutClubRosterPlayer[];
  warnings: string[];
}

export interface WyscoutClubRosterImportResult {
  created: number;
  refreshed: number;
  unchanged: number;
  queued: number;
  reused: number;
}

const headers = (workspaceId: string) => ({'X-Workspace-ID': workspaceId});

const safeWyscoutImageURL = (value: string): string => {
  try {
    const parsed = new URL(value);
    const hostname = parsed.hostname.toLowerCase();
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.port) return '';
    return hostname === 'wyscout.com' || hostname.endsWith('.wyscout.com') ? parsed.toString() : '';
  } catch {
    return '';
  }
};

const mapPlayer = (player: WyscoutClubRosterPlayer): WyscoutClubRosterPlayer => ({
  ...player,
  photoUrl: safeWyscoutImageURL(player.photoUrl),
  existingPlayerId: player.existingPlayerId ?? null,
  existingScope: player.existingScope ?? null,
});

export const wyscoutClubRosterAPI = {
  preview: async (workspaceId: string, playerId: string, signal: AbortSignal): Promise<WyscoutClubRosterPreview> => {
    try {
      const response = await api.get(`/v2/players/${playerId}/wyscout-club-roster`, {headers: headers(workspaceId), signal});
      const preview = response.data.data as WyscoutClubRosterPreview;
      return {...preview, players: preview.players.map(mapPlayer)};
    } catch (error) {
      throw getV2Error(error);
    }
  },
  import: async (
    workspaceId: string,
    playerId: string,
    teamId: number,
    wyscoutPlayerIds: number[],
    months: WyscoutImportMonths,
  ): Promise<WyscoutClubRosterImportResult> => {
    try {
      const response = await api.post(
        `/v2/players/${playerId}/wyscout-club-roster/import`,
        {teamId, wyscoutPlayerIds, months},
        {headers: headers(workspaceId)},
      );
      return response.data.data;
    } catch (error) {
      throw getV2Error(error);
    }
  },
};
